// Whole-collection OOS-last sort + AJAX pagination.
// 1. On collection page load: fetch all server pages (parallel, batched), classify
//    cards by .card__badge containing "sold", sort in-stock first then OOS.
// 2. Cache result in sessionStorage by pathname+sort_by (5 min TTL).
// 3. Intercept pagination link clicks → slice cache for target page → replace
//    grid + pushState → instant <50ms navigation, no server round-trip.
(function () {
  'use strict';
  if (!/^\/collections\/[^/]+\/?$/.test(location.pathname)) return;
  const grid = document.getElementById('product-grid');
  if (!grid) return;

  const params = new URLSearchParams(location.search);
  let currentPage = parseInt(params.get('page') || '1', 10);
  const sectionId = grid.dataset.id || 'product-grid';

  function reveal() {
    document.documentElement.classList.add('oos-sort-ready');
  }

  /* Session cache by collection + sort_by — survives page navigations */
  const CACHE_KEY = 'oos-sort:' + location.pathname + ':' + (params.get('sort_by') || '');
  const CACHE_TTL = 5 * 60 * 1000;
  let cachedAll = null;
  let cachedPageSize = 24;
  function loadCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (Date.now() - d.t > CACHE_TTL) return null;
      return d;
    } catch (e) { return null; }
  }
  function saveCache(cards, pageSize) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), cards, pageSize }));
    } catch (e) {}
  }

  async function fetchPage(pageNum) {
    const u = new URL(location.href);
    u.searchParams.set('page', pageNum);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { credentials: 'same-origin' });
    if (!res.ok) return [];
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = doc.querySelectorAll('.grid__item');
    return Array.from(items).map((item) => {
      const link = item.querySelector('a[href*="/products/"]');
      const m = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      const handle = m ? m[1] : '';
      const badge = item.querySelector('.card__badge');
      const text = (badge && badge.textContent) || '';
      const soldOut = /sold/i.test(text);
      return { handle, html: item.outerHTML, soldOut };
    });
  }

  async function fetchAllPages() {
    const allByPage = [];
    const BATCH = 8;
    for (let batch = 0; batch < 8; batch++) {
      const promises = [];
      for (let i = 0; i < BATCH; i++) {
        const p = batch * BATCH + i + 1;
        promises.push(fetchPage(p).catch(() => []));
      }
      const results = await Promise.all(promises);
      let any = false;
      for (const cards of results) {
        if (cards && cards.length) {
          allByPage.push(cards);
          any = true;
        }
      }
      if (!any) break;
    }
    return allByPage;
  }

  function renderPage(pageNum) {
    if (!cachedAll) return false;
    const inStock = cachedAll.filter((c) => !c.soldOut);
    const oos = cachedAll.filter((c) => c.soldOut);
    const sorted = inStock.concat(oos);
    const start = (pageNum - 1) * cachedPageSize;
    const wanted = sorted.slice(start, start + cachedPageSize);
    if (!wanted.length) return false;
    grid.innerHTML = wanted.map((c) => c.html).join('');
    return true;
  }

  function totalPages() {
    if (!cachedAll || !cachedPageSize) return 1;
    return Math.ceil(cachedAll.length / cachedPageSize);
  }

  function pageUrl(p) {
    const u = new URL(location.href);
    if (p <= 1) u.searchParams.delete('page');
    else u.searchParams.set('page', String(p));
    return u.pathname + u.search;
  }

  /* Build Shopify-style pagination parts: first ± 1, current ± 1, last ± 1, ellipsis between */
  function buildParts(current, total) {
    const set = new Set([1, 2, current - 1, current, current + 1, total - 1, total]);
    const list = Array.from(set).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of list) {
      if (p - prev > 1) out.push({ ellipsis: true });
      out.push({ page: p });
      prev = p;
    }
    return out;
  }

  function renderPagination(current, total) {
    const ul = document.querySelector('.pagination-wrapper .pagination__list');
    if (!ul) return;
    if (total <= 1) { ul.innerHTML = ''; return; }
    let html = '';
    if (current > 1) {
      html += '<li><a href="' + pageUrl(current - 1) + '" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Previous"><i class="fa fa-angle-left"></i></a></li>';
    }
    const parts = buildParts(current, total);
    for (const p of parts) {
      if (p.ellipsis) {
        html += '<li><span class="pagination__item">…</span></li>';
      } else if (p.page === current) {
        html += '<li><span class="pagination__item pagination__item--current" aria-current="page" aria-label="Page ' + p.page + '">' + p.page + '</span></li>';
      } else {
        html += '<li><a href="' + pageUrl(p.page) + '" class="pagination__item link" aria-label="Page ' + p.page + '">' + p.page + '</a></li>';
      }
    }
    if (current < total) {
      html += '<li><a href="' + pageUrl(current + 1) + '" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next"><i class="fa fa-angle-right"></i></a></li>';
    }
    ul.innerHTML = html;
  }

  function bindPagination() {
    const pageLinks = document.querySelectorAll('.pagination-wrapper a.pagination__item');
    pageLinks.forEach((a) => {
      if (a.dataset.aeBound) return;
      a.dataset.aeBound = '1';
      a.addEventListener('click', function (e) {
        if (!cachedAll) return;
        const href = a.getAttribute('href') || '';
        const m = href.match(/[?&]page=(\d+)/);
        const target = m ? parseInt(m[1], 10) : 1;
        if (!target || target < 1 || target > totalPages()) return;
        e.preventDefault();
        navigateTo(target, true);
      });
    });
  }

  function navigateTo(pageNum, push) {
    if (!renderPage(pageNum)) return;
    currentPage = pageNum;
    if (push) {
      const u = new URL(location.href);
      if (pageNum <= 1) u.searchParams.delete('page');
      else u.searchParams.set('page', String(pageNum));
      history.pushState({ page: pageNum }, '', u.toString());
    }
    renderPagination(currentPage, totalPages());
    bindPagination();
    const top = grid.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  window.addEventListener('popstate', function (e) {
    const p = (new URLSearchParams(location.search)).get('page');
    const target = p ? parseInt(p, 10) : 1;
    if (renderPage(target)) {
      currentPage = target;
      renderPagination(currentPage, totalPages());
      bindPagination();
    }
  });

  async function init() {
    /* Fast path */
    const cached = loadCache();
    if (cached && cached.cards && cached.cards.length) {
      cachedAll = cached.cards;
      cachedPageSize = cached.pageSize || 24;
      renderPage(currentPage);
      renderPagination(currentPage, totalPages());
      reveal();
      bindPagination();
      /* Background refresh */
      fetchAllPages().then((allByPage) => {
        if (!allByPage.length) return;
        let ps = 0;
        for (const arr of allByPage) if (arr.length > ps) ps = arr.length;
        const all = [];
        for (const arr of allByPage) all.push(...arr);
        if (all.length) {
          cachedAll = all;
          cachedPageSize = ps || 24;
          saveCache(all, cachedPageSize);
        }
      }).catch(() => {});
      return;
    }

    /* Cold path */
    const allByPage = await fetchAllPages();
    if (!allByPage.length) {
      reveal();
      return;
    }
    let ps = 0;
    for (const arr of allByPage) if (arr.length > ps) ps = arr.length;
    if (!ps) ps = 24;
    cachedAll = [];
    for (const arr of allByPage) cachedAll.push(...arr);
    cachedPageSize = ps;
    saveCache(cachedAll, ps);
    renderPage(currentPage);
    renderPagination(currentPage, totalPages());
    reveal();
    bindPagination();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init().catch(reveal));
  } else {
    init().catch(reveal);
  }
})();
